import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Check,
  Globe2,
  KeyRound,
  Languages,
  MapPinned,
  Save,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { api } from "../api";
import { Button, Card, SectionTitle, Toast } from "./ui";

type GptTextModel = "gpt-5.6-sol" | "gpt-5.6-terra" | "gpt-5.6-luna";
type GptModel = GptTextModel | "gpt-image-2";
type SharedAiSettings = {
  aiRouting: {
    provider: "kimi" | "gptRelay";
    chatModel: GptTextModel;
    deepModel: GptTextModel;
    contentModel: GptTextModel;
    imageModel: "gpt-image-2";
  };
  integrations: {
    kimi: { configured: boolean; masked: string };
    amap: { configured: boolean; masked: string };
    gptRelay: {
      configured: boolean;
      masked: string;
      baseUrl: string;
      verifiedAt: string | null;
      supportedModels: GptModel[];
    };
  };
};

const modelLabel = (model: GptTextModel) =>
  model === "gpt-5.6-terra"
    ? "Terra · 日常均衡"
    : model === "gpt-5.6-sol"
      ? "Sol · 深度旗舰"
      : "Luna · 轻量经济";

export function AdminAiSettings() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState("");
  const [keys, setKeys] = useState({ kimiApiKey: "", amapKey: "" });
  const [relay, setRelay] = useState({ baseUrl: "", apiKey: "" });
  const query = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<SharedAiSettings>("/api/settings"),
  });
  const textModels = useMemo(
    () =>
      query.data?.integrations.gptRelay.supportedModels.filter(
        (model): model is GptTextModel => model !== "gpt-image-2",
      ) || [],
    [query.data],
  );

  useEffect(() => {
    if (query.data?.integrations.gptRelay.baseUrl)
      setRelay((current) => ({
        ...current,
        baseUrl: current.baseUrl || query.data!.integrations.gptRelay.baseUrl,
      }));
  }, [query.data]);

  const saveKeys = useMutation({
    mutationFn: () =>
      api("/api/settings/integrations", {
        method: "PUT",
        body: JSON.stringify(keys),
      }),
    onSuccess: async () => {
      setKeys({ kimiApiKey: "", amapKey: "" });
      setToast("公共密钥已加密保存，所有账户会立即共用这条线路");
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "密钥保存失败"),
  });

  const connectRelay = useMutation({
    mutationFn: () =>
      api<{ integration: { supportedModels: GptModel[] } }>(
        "/api/settings/integrations/gpt-relay",
        {
          method: "PUT",
          body: JSON.stringify({
            baseUrl: relay.baseUrl,
            ...(relay.apiKey ? { apiKey: relay.apiKey } : {}),
          }),
        },
      ),
    onSuccess: async (data) => {
      setRelay((current) => ({ ...current, apiKey: "" }));
      setToast(
        `中转站验证成功，发现 ${data.integration.supportedModels.length} 个已授权模型`,
      );
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "中转站验证失败"),
  });

  const updateRouting = useMutation({
    mutationFn: (value: {
      provider?: "kimi" | "gptRelay";
      chatModel?: GptTextModel;
      deepModel?: GptTextModel;
      contentModel?: GptTextModel;
    }) =>
      api("/api/settings/ai-routing", {
        method: "PUT",
        body: JSON.stringify(value),
      }),
    onSuccess: async () => {
      setToast("公共 AI 场景路由已保存，只影响之后启动的新请求");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["settings"] }),
        queryClient.invalidateQueries({ queryKey: ["assistant-settings"] }),
      ]);
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "模型路由保存失败"),
  });

  const data = query.data;
  return (
    <>
      {toast && <Toast message={toast} />}
      <Card className="admin-shared-services">
        <SectionTitle
          title="公共 API 设置"
          subtitle="只有管理员可以修改；所有账户共用，不会向普通账号返回明文密钥"
          action={<ShieldCheck color="var(--mint)" />}
        />
        <div className="api-settings-grid">
          <section className="settings-modal-section">
            <div className="integration">
              <span className="integration-icon kimi">
                <Bot />
              </span>
              <div>
                <h3>Kimi K3</h3>
                <p>所有账号共用的稳定 AI 线路</p>
                <small
                  className={
                    data?.integrations.kimi.configured ? "connected" : ""
                  }
                >
                  {data?.integrations.kimi.configured ? (
                    <>
                      <Check />
                      已连接 {data.integrations.kimi.masked}
                    </>
                  ) : (
                    "尚未配置"
                  )}
                </small>
              </div>
            </div>
            <div className="field">
              <label>Kimi API Key</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={keys.kimiApiKey}
                  onChange={(event) =>
                    setKeys({ ...keys, kimiApiKey: event.target.value })
                  }
                  placeholder={
                    data?.integrations.kimi.configured
                      ? "输入新 Key 可替换现有配置"
                      : "sk-..."
                  }
                />
              </div>
            </div>
          </section>
          <section className="settings-modal-section">
            <div className="integration">
              <span className="integration-icon amap">
                <MapPinned />
              </span>
              <div>
                <h3>高德地图 Web 服务</h3>
                <p>所有账号共用地点、定位与天气线路</p>
                <small
                  className={
                    data?.integrations.amap.configured ? "connected" : ""
                  }
                >
                  {data?.integrations.amap.configured ? (
                    <>
                      <Check />
                      已连接 {data.integrations.amap.masked}
                    </>
                  ) : (
                    "尚未配置"
                  )}
                </small>
              </div>
            </div>
            <div className="field">
              <label>高德 Web 服务 Key</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={keys.amapKey}
                  onChange={(event) =>
                    setKeys({ ...keys, amapKey: event.target.value })
                  }
                  placeholder="输入 Web 服务 API Key"
                />
              </div>
            </div>
          </section>
          <div className="api-shared-actions">
            <Button
              loading={saveKeys.isPending}
              disabled={!keys.kimiApiKey && !keys.amapKey}
              onClick={() => saveKeys.mutate()}
            >
              <Save />
              加密保存 Kimi / 高德连接
            </Button>
          </div>
          <section className="settings-modal-section relay">
            <SectionTitle
              title="ChatGPT 中转站"
              subtitle="OpenAI-compatible API、联网搜索与 GPT Image 2"
              action={<Globe2 color="var(--lav)" />}
            />
            <div className="integration">
              <span className="integration-icon relay">
                <Zap />
              </span>
              <div>
                <h3>GPT 中转服务</h3>
                <p>所有账号共用 Responses API 与图片生成</p>
                <small
                  className={
                    data?.integrations.gptRelay.configured ? "connected" : ""
                  }
                >
                  {data?.integrations.gptRelay.configured ? (
                    <>
                      <Check />
                      已验证 {data.integrations.gptRelay.masked}
                      {data.integrations.gptRelay.verifiedAt
                        ? ` · ${new Date(data.integrations.gptRelay.verifiedAt).toLocaleString("zh-CN")}`
                        : ""}
                    </>
                  ) : (
                    "尚未连接"
                  )}
                </small>
              </div>
            </div>
            <div className="form-grid">
              <div className="field span-2">
                <label>Base URL（必须包含 /v1）</label>
                <div className="input-icon">
                  <Globe2 />
                  <input
                    type="url"
                    value={relay.baseUrl}
                    onChange={(event) =>
                      setRelay({ ...relay, baseUrl: event.target.value })
                    }
                    placeholder="https://relay.example/v1"
                  />
                </div>
              </div>
              <div className="field span-2">
                <label>GPT 中转 Key</label>
                <div className="input-icon">
                  <KeyRound />
                  <input
                    type="password"
                    value={relay.apiKey}
                    onChange={(event) =>
                      setRelay({ ...relay, apiKey: event.target.value })
                    }
                    placeholder={
                      data?.integrations.gptRelay.configured
                        ? "留空复测现有 Key，输入可替换"
                        : "sk-..."
                    }
                  />
                </div>
              </div>
            </div>
            <div className="relay-warning">
              <ShieldCheck />
              <span>
                <b>第三方服务提醒</b>
                <small>
                  启用 GPT 后，所有账号的 AI
                  请求内容会经过这里填写的中转服务；验证失败不会覆盖旧配置。
                </small>
              </span>
            </div>
            <Button
              loading={connectRelay.isPending}
              disabled={
                !relay.baseUrl ||
                (!relay.apiKey && !data?.integrations.gptRelay.configured)
              }
              onClick={() => connectRelay.mutate()}
            >
              <Check />
              {data?.integrations.gptRelay.configured
                ? "重新测试并保存"
                : "测试连接并保存"}
            </Button>
          </section>
        </div>
      </Card>

      <Card className="admin-engine-card">
        <SectionTitle
          title="公共 AI 引擎"
          subtitle="管理员确定默认线路与场景模型，所有账户的新请求共同使用"
          action={<Sparkles color="var(--lav)" />}
        />
        {query.isPending ? (
          <div className="skeleton" />
        ) : data ? (
          <div className="engine-settings-content">
            <div className="routing-heading">
              <div>
                <b>当前 AI 引擎</b>
                <small>切换只影响之后启动的新请求和后台任务。</small>
              </div>
              <span>
                {data.aiRouting.provider === "gptRelay"
                  ? "GPT · 场景路由"
                  : "Kimi · kimi-k3"}
              </span>
            </div>
            <div className="provider-switch">
              <button
                type="button"
                className={data.aiRouting.provider === "kimi" ? "active" : ""}
                disabled={
                  !data.integrations.kimi.configured || updateRouting.isPending
                }
                onClick={() => updateRouting.mutate({ provider: "kimi" })}
              >
                <Bot />
                <b>Kimi</b>
                <small>
                  {data.integrations.kimi.configured
                    ? "已连接，可作为备用"
                    : "尚未连接"}
                </small>
              </button>
              <button
                type="button"
                className={
                  data.aiRouting.provider === "gptRelay" ? "active" : ""
                }
                disabled={
                  !data.integrations.gptRelay.configured ||
                  updateRouting.isPending
                }
                onClick={() => updateRouting.mutate({ provider: "gptRelay" })}
              >
                <Sparkles />
                <b>GPT 中转</b>
                <small>
                  {data.integrations.gptRelay.configured
                    ? `${textModels.length} 个文本模型已授权`
                    : "请先连接中转站"}
                </small>
              </button>
            </div>
            <div className="scenario-routing">
              <div className="scenario-route">
                <span>
                  <Bot />
                  <b>普通聊天 / 标准思考</b>
                  <small>新对话默认使用此模型</small>
                </span>
                <select
                  className="select"
                  value={data.aiRouting.chatModel}
                  disabled={!textModels.length || updateRouting.isPending}
                  onChange={(event) =>
                    updateRouting.mutate({
                      chatModel: event.target.value as GptTextModel,
                    })
                  }
                >
                  {textModels.map((model) => (
                    <option key={model} value={model}>
                      {modelLabel(model)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="scenario-route">
                <span>
                  <Sparkles />
                  <b>深度思考</b>
                  <small>开启深度后固定使用 Sol</small>
                </span>
                <strong
                  className={
                    data.integrations.gptRelay.supportedModels.includes(
                      "gpt-5.6-sol",
                    )
                      ? "authorized"
                      : "unauthorized"
                  }
                >
                  gpt-5.6-sol ·{" "}
                  {data.integrations.gptRelay.supportedModels.includes(
                    "gpt-5.6-sol",
                  )
                    ? "已授权"
                    : "未授权"}
                </strong>
              </div>
              <div className="scenario-route">
                <span>
                  <Languages />
                  <b>课程与内容任务</b>
                  <small>英法课程、单词、雅思、旅行、资讯和排期</small>
                </span>
                <select
                  className="select"
                  value={data.aiRouting.contentModel}
                  disabled={!textModels.length || updateRouting.isPending}
                  onChange={(event) =>
                    updateRouting.mutate({
                      contentModel: event.target.value as GptTextModel,
                      deepModel: "gpt-5.6-sol",
                    })
                  }
                >
                  {textModels.map((model) => (
                    <option key={model} value={model}>
                      {modelLabel(model)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="scenario-route">
                <span>
                  <Zap />
                  <b>聊天生成图片</b>
                  <small>统一使用 GPT Image 2</small>
                </span>
                <strong
                  className={
                    data.integrations.gptRelay.supportedModels.includes(
                      "gpt-image-2",
                    )
                      ? "authorized"
                      : "unauthorized"
                  }
                >
                  gpt-image-2 ·{" "}
                  {data.integrations.gptRelay.supportedModels.includes(
                    "gpt-image-2",
                  )
                    ? "已授权"
                    : "未授权"}
                </strong>
              </div>
            </div>
          </div>
        ) : (
          <div className="error-box">无法读取公共 AI 配置，请刷新后重试。</div>
        )}
      </Card>
    </>
  );
}
